import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import { isLocalhostHostname, parseSampleRate } from './observabilitySampling';

const DEFAULT_DATADOG_SITE = 'datadoghq.com';
const DEFAULT_SERVICE = 'mento-platform-frontend';
const DEFAULT_LOGS_SESSION_SAMPLE_RATE = 100;
const DEFAULT_RUM_SESSION_SAMPLE_RATE = 20;
const DEFAULT_SESSION_REPLAY_SAMPLE_RATE = 0;
const DEFAULT_TRACE_SAMPLE_RATE = 10;
const MAX_EVENT_SANITIZE_DEPTH = 6;
const SENSITIVE_KEY_PATTERN = /authorization|cookie|password|secret/iu;
const SENSITIVE_QUERY_PATTERN =
  /([?&#][^=&#\s]*(?:code|email|jwt|token)[^=&#\s]*=)[^&#\s]*/giu;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/giu;
const JWT_PATTERN = /\beyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\b/gu;
const MEMBER_ADMIN_PATH_PATTERN = /(\/coach\/members\/)[^/?#]+/giu;
const COACH_ADMIN_PATH_PATTERN = /(\/coach\/manage\/coaches\/)[^/?#]+/giu;
const MATCH_CONFIRM_PATH_PATTERN =
  /(\/api\/v1\/match_decisions\/)[^/?#]+(\/confirm)(?=[/?#]|$)/giu;
const DATADOG_URL_FIELDS = new Set([
  'error.resource.url',
  'http.url',
  'resource.url',
  'view.referrer',
  'view.url',
]);

type DatadogMonitoringConfig = {
  enabled: boolean;
  logsSessionSampleRate: number;
  rumSessionSampleRate: number;
  sessionReplaySampleRate: number;
  traceSampleRate: number;
};

type DatadogMonitoringParams = {
  appEnv: string;
  applicationId?: string;
  clientToken?: string;
  hostname: string;
  logsSessionSampleRate?: string;
  rumSessionSampleRate?: string;
  sessionReplaySampleRate?: string;
  traceSampleRate?: string;
};

const sanitizeString = (value: string) =>
  value
    .replace(SENSITIVE_QUERY_PATTERN, '$1[redacted]')
    .replace(EMAIL_PATTERN, '[redacted-email]')
    .replace(JWT_PATTERN, '[redacted-jwt]');

const sanitizeUrlString = (value: string) =>
  sanitizeString(
    value
      .replace(MEMBER_ADMIN_PATH_PATTERN, '$1:memberId')
      .replace(COACH_ADMIN_PATH_PATTERN, '$1:coachId')
      .replace(MATCH_CONFIRM_PATH_PATTERN, '$1:decisionId$2'),
  );

const sanitizeUrl = (value: string | URL) => {
  if (typeof value === 'string') return sanitizeUrlString(value);
  return new URL(sanitizeUrlString(value.toString()));
};

const isSensitiveKey = (key: string) => {
  const normalizedKey = key.toLowerCase();

  return (
    SENSITIVE_KEY_PATTERN.test(normalizedKey) ||
    normalizedKey === 'email' ||
    normalizedKey.endsWith('email') ||
    normalizedKey.endsWith('email_address') ||
    normalizedKey.endsWith('jwt') ||
    normalizedKey.endsWith('token')
  );
};

/* eslint-disable no-param-reassign -- Datadog beforeSend requires mutating the event before it is sent. */
const sanitizeDatadogEvent = (
  value: unknown,
  depth = 0,
  path: readonly string[] = [],
): unknown => {
  if (typeof value === 'string') return sanitizeString(value);
  if (typeof value !== 'object' || value === null) return value;
  if (depth >= MAX_EVENT_SANITIZE_DEPTH) return '[truncated]';

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      value[index] = sanitizeDatadogEvent(item, depth + 1, path);
    });
    return value;
  }

  Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
    const itemPath = [...path, key];
    let sanitizedItem: unknown;
    if (isSensitiveKey(key)) sanitizedItem = '[redacted]';
    else if (
      DATADOG_URL_FIELDS.has(itemPath.join('.')) &&
      (typeof item === 'string' || item instanceof URL)
    )
      sanitizedItem = sanitizeUrl(item);
    else sanitizedItem = sanitizeDatadogEvent(item, depth + 1, itemPath);
    (value as Record<string, unknown>)[key] = sanitizedItem;
  });

  return value;
};
/* eslint-enable no-param-reassign */

export const sanitizeDatadogEventForMonitoring = (event: unknown) => {
  sanitizeDatadogEvent(event);
  return true;
};

export const getDatadogMonitoringConfig = ({
  appEnv,
  applicationId,
  clientToken,
  hostname,
  logsSessionSampleRate,
  rumSessionSampleRate,
  sessionReplaySampleRate,
  traceSampleRate,
}: DatadogMonitoringParams): DatadogMonitoringConfig => {
  const enabled = Boolean(
    appEnv !== 'local' &&
    !isLocalhostHostname(hostname) &&
    applicationId &&
    clientToken,
  );

  return {
    enabled,
    logsSessionSampleRate: parseSampleRate(
      logsSessionSampleRate,
      DEFAULT_LOGS_SESSION_SAMPLE_RATE,
    ),
    rumSessionSampleRate: parseSampleRate(
      rumSessionSampleRate,
      DEFAULT_RUM_SESSION_SAMPLE_RATE,
    ),
    sessionReplaySampleRate: parseSampleRate(
      sessionReplaySampleRate,
      DEFAULT_SESSION_REPLAY_SAMPLE_RATE,
    ),
    traceSampleRate: parseSampleRate(
      traceSampleRate,
      DEFAULT_TRACE_SAMPLE_RATE,
    ),
  };
};

export const initDatadogMonitoring = () => {
  const applicationId = import.meta.env.VITE_DATADOG_APPLICATION_ID;
  const clientToken = import.meta.env.VITE_DATADOG_CLIENT_TOKEN;
  const config = getDatadogMonitoringConfig({
    appEnv: import.meta.env.VITE_APP_ENV,
    applicationId,
    clientToken,
    hostname: window.location.hostname,
    logsSessionSampleRate: import.meta.env
      .VITE_DATADOG_LOGS_SESSION_SAMPLE_RATE,
    rumSessionSampleRate: import.meta.env.VITE_DATADOG_RUM_SESSION_SAMPLE_RATE,
    sessionReplaySampleRate: import.meta.env
      .VITE_DATADOG_SESSION_REPLAY_SAMPLE_RATE,
    traceSampleRate: import.meta.env.VITE_DATADOG_TRACE_SAMPLE_RATE,
  });

  if (!config.enabled || !applicationId || !clientToken) {
    return config;
  }

  const sharedConfig = {
    clientToken,
    env: import.meta.env.VITE_APP_ENV,
    ...(import.meta.env.VITE_DATADOG_PROXY_URL
      ? { proxy: import.meta.env.VITE_DATADOG_PROXY_URL }
      : {}),
    service: import.meta.env.VITE_DATADOG_SERVICE || DEFAULT_SERVICE,
    site: import.meta.env.VITE_DATADOG_SITE || DEFAULT_DATADOG_SITE,
    version: import.meta.env.VITE_APP_VERSION,
  };

  datadogRum.init({
    ...sharedConfig,
    allowedTracingUrls: [import.meta.env.VITE_BASE_API_URL],
    applicationId,
    beforeSend: sanitizeDatadogEventForMonitoring,
    defaultPrivacyLevel: 'mask',
    sessionReplaySampleRate: config.sessionReplaySampleRate,
    sessionSampleRate: config.rumSessionSampleRate,
    silentMultipleInit: true,
    traceSampleRate: config.traceSampleRate,
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
  });

  datadogLogs.init({
    ...sharedConfig,
    beforeSend: sanitizeDatadogEventForMonitoring,
    forwardErrorsToLogs: true,
    sessionSampleRate: config.logsSessionSampleRate,
    silentMultipleInit: true,
  });

  return config;
};
